'use strict';

import { useState, useEffect, useCallback } from 'react';
import BoardStore from '@stores/BoardStore';
import BoardActions from '@actions/BoardActions';
import confirm from '@utils/Confirm';
import {
    getResolvedPlanUuid,
    addPlanToFavorites,
    removeFromFavorites
} from '@utils/planUtils';

/**
 * Custom hook for managing board/favoriting functionality
 * Provides state and operations for adding/removing plans from favorites
 */
export const useFavorites = (plan, savePlan, profile = null) => {
    const [boardContent, setBoardContent] = useState(null);
    const [isInBoards, setIsInBoards] = useState(false);

    /**
     * Get board content for a plan UUID
     */
    const getBoardContentByUuid = useCallback((uuid) => {
        if (!uuid) return null;
        
        const boards = BoardStore.getBoardsByResourceId(uuid);
        const contents = boards.reduce((carry, board) => {
            const bcs = board.contents.filter(
                ({resource_id}) => resource_id === uuid
            );
            return carry.concat(bcs);
        }, []);
        
        // Prioritize dietitian recommendations, but return any board content
        const dietitianRec = contents.find(c => c.dietitian_recommendation);
        if (dietitianRec) {
            return dietitianRec;
        }
        
        return contents[0] || null;
    }, []);


    /**
     * Handle favorite button click - check unfavorite first, then save virtual plans if favoriting
     */
    const handleFavoriteClick = useCallback(async () => {
        try {
            // Get the real UUID for virtual plans that were saved
            const resolvedUuid = getResolvedPlanUuid(plan);
            const planToUse = { ...plan, uuid: resolvedUuid };

            // If plan is already in boards, user wants to unfavorite - check confirmation FIRST
            if (isInBoards) {
                // Confirm before removing from favorites
                const confirmed = await confirm(
                    'Remove this meal plan from your favorites?',
                    {
                        acceptText: 'Remove',
                        rejectText: 'Cancel'
                    }
                );

                if (!confirmed) {
                    return; // User cancelled
                }

                removeFromFavorites(planToUse);
                return;
            }

            // User wants to favorite - check if virtual and save first
            if (!plan.links?.self) {
                const result = await savePlan();

                if (result.success && result.plan) {
                    addPlanToFavorites(result.plan, profile);
                }
                return;
            }

            // Saved plan that's not in boards - just add to favorites
            addPlanToFavorites(plan, profile);
        } catch (error) {
            throw error;
        }
    }, [plan, savePlan, isInBoards, profile]);

    /**
     * Check for dietitian recommendations before unfavoriting
     */
    const checkRecommendations = useCallback(async () => {
        if (boardContent?.dietitian_recommendation) {
            await confirm("Plans recommended by your dietitian cannot be removed from favorites.", {
                acceptText: 'OK',
                rejectText: null // Hide the cancel button for alert behavior
            });
            return true; // Prevent unfavoriting
        }
        return false; // Allow unfavoriting
    }, [boardContent]);

    // Load boards on mount
    useEffect(() => {
        // Use setTimeout to avoid dispatching during another dispatch
        setTimeout(() => {
            BoardActions.loadBoards();
        }, 0);
    }, []);

    // Track board membership for the current plan
    useEffect(() => {
        const handleBoardChange = () => {
            if (plan?.uuid) {
                // Get the real UUID to check (handles virtual plans that were saved)
                const uuidToCheck = getResolvedPlanUuid(plan);

                const boards = BoardStore.getBoardsByResourceId(uuidToCheck);
                setIsInBoards(boards.length > 0);
                setBoardContent(getBoardContentByUuid(uuidToCheck));
            }
        };

        BoardStore.addChangeListener(handleBoardChange);
        handleBoardChange(); // Initial check

        return () => BoardStore.removeChangeListener(handleBoardChange);
    }, [plan?.uuid, getBoardContentByUuid]);

    return {
        // State
        boardContent,
        isInBoards,

        // Actions
        handleFavoriteClick,
        checkRecommendations,

        // Derived values for props
        boarded: isInBoards
    };
};